import { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  DocumentData,
} from "firebase/firestore";
import { db } from "../firebase";

type Event = {
  id: string;
  type?: string;
  price?: number;
  symbol?: string;
  label?: string;
  value?: number;
  timestamp: {
    toDate: () => Date;
  } | null;
};

interface EventTableProps {
  collectionName: string;
  title: string;
  rowLimit?: number;
}

export default function EventTable({
  collectionName,
  title,
  rowLimit = 10,
}: EventTableProps) {
  const [events, setEvents] = useState<Event[]>([]);

  useEffect(() => {
    if (!collectionName || collectionName.trim() === "") {
      console.error("❌ Invalid collectionName:", collectionName);
      return;
    }

    const q = query(
      collection(db, collectionName),
      orderBy("timestamp", "desc"),
      limit(rowLimit)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data: Event[] = snapshot.docs.map((doc) => {
        const docData = doc.data() as DocumentData;
        return {
          id: doc.id,
          type: docData.type,
          price: docData.price,
          symbol: docData.symbol,
          label: docData.label,
          value: docData.value,
          timestamp: docData.timestamp || null,
        };
      });
      setEvents(data);
    });

    return () => unsubscribe();
  }, [collectionName, rowLimit]);

  return (
    <div className="mb-6 p-6 bg-gray-800 shadow-xl rounded-xl overflow-x-auto border border-gray-700">
      <h2 className="text-2xl font-bold mb-4 text-white">{title}</h2>
      <table className="min-w-full border border-gray-600 text-sm">
        <thead className="bg-gray-700">
          <tr>
            <th className="px-4 py-3 text-left text-gray-200 font-semibold">Type/Label</th>
            <th className="px-4 py-3 text-left text-gray-200 font-semibold">Symbol</th>
            <th className="px-4 py-3 text-left text-gray-200 font-semibold">Price/Value</th>
            <th className="px-4 py-3 text-left text-gray-200 font-semibold">Timestamp</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event, idx) => (
            <tr
              key={event.id}
              className={`border-t border-gray-600 ${idx % 2 === 0 ? "bg-gray-800" : "bg-gray-750"} hover:bg-gray-700 transition-colors duration-200`}
            >
              <td className="px-4 py-3 text-gray-300">{event.type || event.label || "—"}</td>
              <td className="px-4 py-3 text-gray-300">{event.symbol || "—"}</td>
              <td className="px-4 py-3 text-gray-300">
                {typeof event.price === "number"
                  ? `$${event.price.toFixed(2)}`
                  : event.value ?? "—"}
              </td>
              <td className="px-4 py-3 text-gray-300">
                {event.timestamp?.toDate
                  ? event.timestamp.toDate().toLocaleString()
                  : "—"}
              </td>
            </tr>
          ))}
          {events.length === 0 && (
            <tr>
              <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                No events found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
